'use client';

import { useState, useMemo, useCallback } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { useProxyStore, type HttpRequest } from '../store/proxy-store';
import {
  useOptionalTargetId,
  useAppTargetId,
  useTargetType,
} from '../store/projectStore';
import { useTargetSignals } from './api/useTargetSignals';
import { terraProxyApiClient } from '../clients/testerClient';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { formatCategoryForDisplay } from '@terra/ui-design-system';

// Endpoint data type from the unified assets hook (subset of full endpoint)
export type EndpointSearchData = {
  id: string;
  description: string | null;
  path: string;
  host: string;
  port: number | null;
  url: string;
  request: {
    method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH';
    pathParams?: string[];
    bodySchema?: string | null;
  };
  graphqlOperationName?: string | null;
  codeGraphId: string | null;
  credentialRoles?: string[];
  updatedAt: Date;
};

export type SearchResultType =
  | 'endpoint'
  | 'signal'
  | 'request'
  | 'webpage'
  | 'action';

type BaseSearchResult = {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle: string;
  score: number;
};

type EndpointSearchResult = BaseSearchResult & {
  type: 'endpoint';
  data: EndpointSearchData;
};

type SignalSearchResult = BaseSearchResult & {
  type: 'signal';
  data: GroupedSignal;
};

type RequestSearchResult = BaseSearchResult & {
  type: 'request';
  data: HttpRequest;
};

type WebPageSearchResult = BaseSearchResult & {
  type: 'webpage';
  data: { id: string; url: string; description?: string };
};

type ActionSearchResult = BaseSearchResult & {
  type: 'action';
  data: { actionId: string; handler: () => void };
};

export type SearchResult =
  | EndpointSearchResult
  | SignalSearchResult
  | RequestSearchResult
  | WebPageSearchResult
  | ActionSearchResult;

type SearchResultGroup = {
  type: SearchResultType;
  label: string;
  results: SearchResult[];
};

const RESULT_LIMIT_PER_TYPE = 5;

function scoreMatch({ text, query }: { text: string; query: string }): number {
  if (!text || !query) return 0;

  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.toLowerCase();

  // Exact match gets highest score
  if (normalizedText === normalizedQuery) return 100;

  // Starts with query
  if (normalizedText.startsWith(normalizedQuery)) return 75;

  // Contains query as a word boundary
  const wordBoundaryRegex = new RegExp(
    `\\b${escapeRegex(normalizedQuery)}`,
    'i',
  );
  if (wordBoundaryRegex.test(normalizedText)) return 50;

  // Contains query anywhere
  if (normalizedText.includes(normalizedQuery)) return 25;

  return 0;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function searchRequests({
  requests,
  query,
}: {
  requests: HttpRequest[];
  query: string;
}): RequestSearchResult[] {
  if (!query) return [];

  const results: RequestSearchResult[] = [];

  for (const request of requests) {
    let score = 0;

    // Score URL
    score += scoreMatch({ text: request.url || '', query }) * 1.5;

    // Score host
    score += scoreMatch({ text: request.host || '', query });

    // Score method
    score += scoreMatch({ text: request.method || '', query }) * 0.5;

    // Score status code
    if (request.status) {
      score += scoreMatch({ text: String(request.status), query }) * 0.3;
    }

    // Score comments (user annotations)
    if (request.comment) {
      score += scoreMatch({ text: request.comment, query }) * 2;
    }

    // Score tags
    if (request.tags) {
      for (const tag of request.tags) {
        score += scoreMatch({ text: tag, query }) * 1.5;
      }
    }

    if (score > 0) {
      // Safely extract pathname - url might be relative or absolute
      let path: string;
      try {
        path = new URL(request.url).pathname;
      } catch {
        // If URL parsing fails, use the url as-is (likely already a path)
        path = request.url.split('?')[0];
      }
      results.push({
        id: request.id,
        type: 'request',
        title: `${request.method} ${path}`,
        subtitle: `${request.status || '...'} • ${request.host}`,
        score,
        data: request,
      });
    }
  }

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, RESULT_LIMIT_PER_TYPE);
}

type QuickAction = {
  id: string;
  title: string;
  subtitle: string;
  keywords: string[];
  handler: () => void;
};

function searchActions({
  actions,
  query,
}: {
  actions: QuickAction[];
  query: string;
}): ActionSearchResult[] {
  if (!query) return [];

  const results: ActionSearchResult[] = [];

  for (const action of actions) {
    let score = 0;

    // Score title
    score += scoreMatch({ text: action.title, query }) * 1.5;

    // Score keywords
    for (const keyword of action.keywords) {
      score += scoreMatch({ text: keyword, query }) * 2;
    }

    if (score > 0) {
      results.push({
        id: action.id,
        type: 'action',
        title: action.title,
        subtitle: action.subtitle,
        score,
        data: { actionId: action.id, handler: action.handler },
      });
    }
  }

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, RESULT_LIMIT_PER_TYPE);
}

type UseGlobalSearchOptions = {
  onNavigateToEndpoint?: (endpoint: EndpointSearchData) => void;
  onNavigateToSignal?: (signal: GroupedSignal) => void;
  onNavigateToRequest?: (request: HttpRequest) => void;
  onOpenSettings?: () => void;
  onOpenHistory?: () => void;
  onOpenIntercept?: () => void;
  onOpenReplay?: () => void;
};

export function useGlobalSearch(options: UseGlobalSearchOptions = {}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query, 150);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Get data from stores
  const targetId = useOptionalTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const { requests, requestOrder } = useProxyStore();

  // Backend search for endpoints (assets) - only when there's a search query
  const { data: endpointsData, isLoading: isLoadingEndpoints } = useQuery({
    queryKey: ['global-search-endpoints', appTargetId, debouncedQuery],
    queryFn: async () => {
      if (!appTargetId || !debouncedQuery.trim()) return [];
      try {
        const result = await terraProxyApiClient.searchAssetsPreview(
          appTargetId,
          {
            url: debouncedQuery.trim(),
            type: 'endpoints',
            limit: RESULT_LIMIT_PER_TYPE,
          },
        );
        if (!result?.assets) return [];
        // Map assets to endpoint search data format
        return result.assets
          .filter(
            (
              asset,
            ): asset is Extract<
              (typeof result.assets)[number],
              { assetType: 'endpoint' }
            > => asset.assetType === 'endpoint',
          )
          .map((asset) => ({
            id: asset.id,
            description: asset.description,
            path: asset.path,
            host: asset.host,
            port: asset.port,
            url: asset.url,
            request: asset.request,
            graphqlOperationName: asset.graphqlOperationName,
            codeGraphId: asset.codeGraphId,
            credentialRoles: asset.credentialRoles,
            updatedAt: asset.updatedAt,
          }));
      } catch (error) {
        console.error('[GlobalSearch] Error fetching endpoints:', error);
        return [];
      }
    },
    enabled: !!appTargetId && !!debouncedQuery.trim(),
    staleTime: 30000,
  });
  const endpoints = useMemo(() => endpointsData ?? [], [endpointsData]);

  // Backend search for signals/signals - only when there's a search query
  const { data: signalsData, isLoading: isLoadingSignals } = useTargetSignals({
    targetId,
    targetType,
    enabled: !!targetId && !!debouncedQuery.trim(),
    limit: RESULT_LIMIT_PER_TYPE,
    filters: {
      description: debouncedQuery.trim(),
    },
    queryKeySuffix: debouncedQuery,
  });
  const signals = useMemo(() => signalsData ?? [], [signalsData]);

  // Convert requests record to array
  const requestsArray = useMemo(() => {
    return requestOrder.map((id) => requests[id]).filter(Boolean);
  }, [requests, requestOrder]);

  // Build quick actions
  const quickActions: QuickAction[] = useMemo(
    () => [
      {
        id: 'action-settings',
        title: 'Open Settings',
        subtitle: 'Application and proxy settings',
        keywords: ['settings', 'config', 'preferences', 'options'],
        handler: () => options.onOpenSettings?.(),
      },
      {
        id: 'action-history',
        title: 'HTTP History',
        subtitle: 'View all captured requests',
        keywords: ['history', 'requests', 'http', 'traffic'],
        handler: () => options.onOpenHistory?.(),
      },
      {
        id: 'action-intercept',
        title: 'Intercept',
        subtitle: 'Intercept and modify requests',
        keywords: ['intercept', 'modify', 'edit', 'proxy'],
        handler: () => options.onOpenIntercept?.(),
      },
      {
        id: 'action-replay',
        title: 'Replay',
        subtitle: 'Replay and modify HTTP requests',
        keywords: ['replay', 'repeat', 'send', 'resend'],
        handler: () => options.onOpenReplay?.(),
      },
    ],
    [options],
  );

  // Perform search - combining backend results with client-side search
  const searchResults = useMemo((): SearchResultGroup[] => {
    if (!debouncedQuery.trim()) return [];

    const groups: SearchResultGroup[] = [];

    // Search actions first (quick commands) - client-side only
    const actionResults = searchActions({
      actions: quickActions,
      query: debouncedQuery,
    });
    if (actionResults.length > 0) {
      groups.push({
        type: 'action',
        label: 'Quick Actions',
        results: actionResults,
      });
    }

    // Endpoints - backend already filtered, just format results
    if (endpoints.length > 0) {
      const endpointResults: EndpointSearchResult[] = endpoints.map(
        (endpoint) => ({
          id: endpoint.id,
          type: 'endpoint' as const,
          title: `${endpoint.request?.method || 'GET'} ${endpoint.path || endpoint.url}`,
          subtitle: endpoint.host || '',
          score: 100, // Backend already ranked/filtered
          data: endpoint,
        }),
      );
      groups.push({
        type: 'endpoint',
        label: 'Endpoints',
        results: endpointResults,
      });
    }

    // Signals (signals) - backend already filtered, just format results
    if (signals.length > 0) {
      const signalResults: SignalSearchResult[] = signals.map((signal) => ({
        id: signal.id,
        type: 'signal' as const,
        title:
          signal.description ||
          formatCategoryForDisplay(signal.category) ||
          'Signal',
        subtitle: `${signal.status || signal.stage || 'NEW'} • ${
          formatCategoryForDisplay(signal.category) || 'Unknown'
        }`,
        score: 100, // Backend already ranked/filtered
        data: signal,
      }));
      groups.push({
        type: 'signal',
        label: 'Signals',
        results: signalResults,
      });
    }

    // Search HTTP requests - client-side only (local proxy data)
    const requestResults = searchRequests({
      requests: requestsArray,
      query: debouncedQuery,
    });
    if (requestResults.length > 0) {
      groups.push({
        type: 'request',
        label: 'HTTP Requests',
        results: requestResults,
      });
    }

    return groups;
  }, [debouncedQuery, endpoints, signals, requestsArray, quickActions]);

  // Flatten results for keyboard navigation
  const flatResults = useMemo(() => {
    return searchResults.flatMap((group) => group.results);
  }, [searchResults]);

  // Reset selection when results change
  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery);
    setSelectedIndex(0);
  }, []);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      switch (result.type) {
        case 'endpoint':
          options.onNavigateToEndpoint?.(result.data);
          break;
        case 'signal':
          options.onNavigateToSignal?.(result.data);
          break;
        case 'request':
          options.onNavigateToRequest?.(result.data);
          break;
        case 'action':
          result.data.handler();
          break;
      }
    },
    [options],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < flatResults.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : flatResults.length - 1,
          );
          break;
        case 'Enter': {
          event.preventDefault();
          const selected = flatResults[selectedIndex];
          if (selected) {
            handleSelect(selected);
          }
          break;
        }
      }
    },
    [flatResults, selectedIndex, handleSelect],
  );

  const clearSearch = useCallback(() => {
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const isSearching =
    query !== debouncedQuery || isLoadingEndpoints || isLoadingSignals;

  return {
    query,
    setQuery: handleQueryChange,
    searchResults,
    flatResults,
    selectedIndex,
    setSelectedIndex,
    handleKeyDown,
    handleSelect,
    clearSearch,
    isSearching,
    hasResults: flatResults.length > 0,
  };
}
